import Icon from 'components/Common/Icon';
import TooltipFixedPosition from 'components/TooltipFixedPosition/TooltipFixedPosition';
import React from 'react';
import intl from 'react-intl-universal';
import { Link } from 'react-router-dom';
import { compose, lifecycle, } from 'recompose';
import { LOCATION } from 'constants/tooltipFixedPositionConstants';
import SingleRecordingInterface from 'interfaces/SingleRecording';
import { formatDateAtTimeFromUnix } from 'utils/date-time-utils';
import { showLoaderIfTrue } from 'components/LoadingIndicator/LoadingIndicator';
import styles from './SingleRecordingFrame.scss';

export const SingleRecordingFrameSFC: React.FunctionComponent<SingleRecordingInterface> = (props) => {
  if (!props.singleRecordingData) {
    return <React.Fragment />;
  }
  const {
    can_wlater, date_created, default_meeting, download_url,
    iframe, meeting, meeting_id, name, owner, views, description
  }
    = props.singleRecordingData;

  const {
    addToWatchList,
    canUserModifyRecording,
    confirmDelete,
    isOnWatchList,
    notFound,
    notAuthorized,
    openEditModal,
    openShareModal,
    recordingId,
    removeFromWatchList,
  } = props;

  if (notFound || notAuthorized) {
    return (
      <div className={styles.container}>
        <div className={styles.messageOnly}>
          <Icon classes={['icon-record']} />
          <h4 className="stacked">
            {notFound ? intl.get('recordingNotFound') : intl.get('recordingNoAccess')}
          </h4>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.videoFrame}>
        <iframe src={iframe} scrolling="no" allowFullScreen={true} />
      </div>
      <div className={styles.name}>
        {name}
      </div>
      {description ? (
        <div className={styles.descriptionContainer}>
          {description}
        </div>
      ) : null}
      <div className={styles.timeAndViewsContainer}>
        <div>
          {formatDateAtTimeFromUnix(date_created * 1000)}
        </div>
        <div className={styles.verticalDivider} />
        <div className={styles.viewsContainer}>
          <div className={styles.eye} />
          {views}
        </div>
      </div>
      <div className={styles.horizontalDivider} />
      <div className={styles.videoDetails}>
        <div className={styles.ownerAndVmr}>
          <div>{owner}</div>
          {meeting_id !== default_meeting ? (
            <div>
              <Link to={`/recordingsFeed?meeting=${encodeURIComponent(meeting_id)}`}>
                {meeting}
              </Link>
            </div>
          ) : null}
        </div>

        <div className={styles.buttonBar}>
          {
            canUserModifyRecording &&
            <div className={styles.button}>
              <button
                onClick={() => {
                  openEditModal(recordingId);
                }}
                name="btnEdit"
                role="button"
                type="button"
                data-test="singleRec.editBtn"
              >
                <Icon classes={['icon-edit']} />
              </button>
              <TooltipFixedPosition
                label={intl.get('editRecordingTitle')}
                location={LOCATION.TOP}
                offset={{ top: -5 }}
              />
            </div>
          }

          {
            canUserModifyRecording &&
            <div className={styles.button}>
              <a href={`${download_url}`} target="_blank">
                <button name="btnDownload" role="button" type="button" data-test="singleRec.downloadBtn">
                  <Icon classes={['icon-download']} />
                </button>
                <TooltipFixedPosition
                  label={intl.get('download')}
                  location={LOCATION.TOP}
                  offset={{ top: -5 }}
                />
              </a>
            </div>
          }

          {
            canUserModifyRecording &&
            <div className={styles.button}>
              <button name="btnDelete" onClick={confirmDelete} role="button" type="button" data-test="singleRec.deleteBtn">
                <Icon classes={['icon-trash']} />
              </button>
              <TooltipFixedPosition
                label={intl.get('deleteRecording')}
                location={LOCATION.TOP}
                offset={{ top: -5 }}
              />
            </div>
          }

          {<div className={styles.verticalDivider} style={{ height: '24px' }} />}

          {
            canUserModifyRecording &&
            <div className={styles.button}>
              <button
                name="btnShare"
                onClick={() => {
                  openShareModal(recordingId);
                }}
                role="button"
                type="button"
                data-test="singleRec.shareBtn"
              >
                <Icon classes={['icon-share-alt']} />
              </button>
              <TooltipFixedPosition
                label={intl.get('share')}
                location={LOCATION.TOP}
                offset={{ top: -5 }}
              />
            </div>
          }

          {can_wlater && (
            <div className={styles.button}>
              <button
                name="btnRemove"
                onClick={() => {
                  if (addToWatchList && recordingId) {
                    isOnWatchList ? removeFromWatchList(recordingId) : addToWatchList(recordingId);
                  }
                }}
                role="button"
                type="button"
                data-test="singleRec.addBtn"
              >
                <Icon classes={[isOnWatchList ? 'icon-zoom-out' : 'icon-zoom-in']} />
              </button>
              <TooltipFixedPosition
                label={intl.get(isOnWatchList ? 'removeFromWatchList' : 'addToWatchList')}
                location={LOCATION.LEFT}
                offset={{ top: -5 }}
              />
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

const composed = compose(
  lifecycle<SingleRecordingInterface, {}>({
    componentDidMount() {
      const { recordingId } = this.props;
      if (recordingId && recordingId.length) {
        this.props.fetchSingleRecording(recordingId);
      }
    },
    componentWillReceiveProps(nextProps: SingleRecordingInterface) {
      const { fetchSingleRecording } = this.props;
      if (this.props.recordingId !== nextProps.recordingId) {
        fetchSingleRecording(nextProps.recordingId);
      }
    }
  }),
  showLoaderIfTrue<SingleRecordingInterface>(
    ({ isFetching, isAddingToWatchList }) => isFetching || isAddingToWatchList,
    styles.container)
);

export const SingleRecordingFrame = composed(SingleRecordingFrameSFC);
